/**
 * URS-F488 WorkingHoursPanel
 *
 * Configuracion del horario laboral del centro (apertura/cierre por dia
 * de la semana + festivos). Alimenta el calculo de eficiencia de agenda
 * en Estadisticas.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { errText } from '../../utils/errors';
import { FloppyDisk } from '@phosphor-icons/react';
import { HolidaysByYear } from './HolidaysByYear';
import { refreshHolidays } from '../agenda/useHolidays';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WEEKDAY_LABELS_ES = { '1': 'Lunes', '2': 'Martes', '3': 'Miércoles', '4': 'Jueves', '5': 'Viernes', '6': 'Sábado', '7': 'Domingo' };
const WEEKDAY_LABELS_EN = { '1': 'Monday', '2': 'Tuesday', '3': 'Wednesday', '4': 'Thursday', '5': 'Friday', '6': 'Saturday', '7': 'Sunday' };

export default function WorkingHoursPanel({ lang = 'es' }) {
  const [wh, setWh] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API_URL}/api/config/working-hours`, { withCredentials: true });
      setWh(r.data || null);
    } catch (err) {
      toast.error(errText(err));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const updateWeekday = (wk, field, value) => {
    setWh(p => ({ ...p, weekdays: { ...p.weekdays, [wk]: { ...p.weekdays[wk], [field]: value } } }));
  };


  const save = async () => {
    if (!wh) return;
    setSaving(true);
    try {
      const payload = {
        slot_minutes: wh.slot_minutes || 15,
        weekdays: wh.weekdays,
        holidays: wh.holidays || [],
      };
      await axios.put(`${API_URL}/api/config/working-hours`, payload, { withCredentials: true });
      refreshHolidays();
      toast.success(lang === 'es' ? 'Horario laboral actualizado' : 'Working hours updated');
      await load();
    } catch (err) {
      toast.error(errText(err));
    }
    setSaving(false);
  };

  const labels = lang === 'es' ? WEEKDAY_LABELS_ES : WEEKDAY_LABELS_EN;

  if (loading || !wh) return <p className="text-sm text-stone-400 text-center py-6">{lang === 'es' ? 'Cargando…' : 'Loading…'}</p>;

  return (
    <div className="space-y-6" data-testid="working-hours-panel">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-sm font-medium text-stone-900">{lang === 'es' ? 'Horario laboral del centro' : 'Center working hours'}</h3>
          <p className="text-xs text-stone-500 mt-1 max-w-2xl">
            {lang === 'es'
              ? 'Configura la apertura y cierre por día de la semana + los festivos anuales. Alimenta el cálculo de "Eficiencia de Agenda" en Estadísticas — los días no laborables y festivos no cuentan en las métricas.'
              : 'Configure daily open/close hours and yearly holidays. Feeds the Agenda Efficiency KPIs in Statistics — non-working days and holidays are excluded from computations.'}
          </p>
        </div>
        <Button onClick={save} disabled={saving} data-testid="save-working-hours-btn" className="bg-[#1f3e80] hover:bg-[#2a4f8a] text-white gap-2 rounded-sm">
          <FloppyDisk size={14} />{saving ? '…' : (lang === 'es' ? 'Guardar' : 'Save')}
        </Button>
      </div>

      <div className="space-y-2">
        <h4 className="text-xs font-semibold uppercase tracking-wider text-stone-500">{lang === 'es' ? 'Días laborables' : 'Weekdays'}</h4>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-40">{lang === 'es' ? 'Día' : 'Day'}</TableHead>
              <TableHead className="w-32 text-center">{lang === 'es' ? 'Activo' : 'Enabled'}</TableHead>
              <TableHead className="w-40">{lang === 'es' ? 'Apertura' : 'Open'}</TableHead>
              <TableHead className="w-40">{lang === 'es' ? 'Cierre' : 'Close'}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {['1','2','3','4','5','6','7'].map(wk => {
              const cfg = wh.weekdays?.[wk] || { open: '08:00', close: '21:00', enabled: false };
              return (
                <TableRow key={wk} data-testid={`wh-row-${wk}`}>
                  <TableCell className="text-sm text-stone-800">{labels[wk]}</TableCell>
                  <TableCell className="text-center">
                    <input
                      type="checkbox"
                      checked={!!cfg.enabled}
                      onChange={e => updateWeekday(wk, 'enabled', e.target.checked)}
                      className="h-4 w-4 accent-emerald-600"
                      data-testid={`wh-enabled-${wk}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="time"
                      value={cfg.open}
                      onChange={e => updateWeekday(wk, 'open', e.target.value)}
                      className="w-28 font-mono text-sm"
                      disabled={!cfg.enabled}
                      data-testid={`wh-open-${wk}`}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      type="time"
                      value={cfg.close}
                      onChange={e => updateWeekday(wk, 'close', e.target.value)}
                      className="w-28 font-mono text-sm"
                      disabled={!cfg.enabled}
                      data-testid={`wh-close-${wk}`}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <HolidaysByYear holidays={wh.holidays || []} onChange={(list) => setWh(p => ({ ...p, holidays: list }))} lang={lang} />
    </div>
  );
}
